import   React               ,
       { useEffect           ,
         useState            }  from "react";

import   classNames             from "classnames";

import   styles                 from "./Cart.module.scss";

import   Trigger                from "../../widget/actuators/Trigger";
import   Field                  from "../../widget/inputs/Field";
import   Box                    from "../../widget/windows/Box";
import   AnimatedWaitingIcon2   from "../../widget/animations/animatedWaitingIcon2";

import   BankChoice             from "./BankChoice";

import { Product              }  from "../../../dataTypes/productManagment/types";
import { Cart as CartData     ,
         CartItem             }  from "../../../dataTypes/purchaseManagment/types";

import { getProducts          }  from "../../../backendServices/productManagment/api_product";
import { getCart              ,
         addItemToCart         ,
         removeItemFromCart    ,
         changeQuantityOfItem  ,
         createPayment         }  from "../../../backendServices/purchaseManagment/api";

import { useTypedSelector     }  from "../../../store/useTypeSelector";
import { RootState            }  from "../../../store/generalReducer";

export interface CartProps {
  email?: string;
}

// Panier (achat non payé) du créateur connecté, ou d'un créateur ciblé via `email`
// (fiche ouverte par un superadmin). Actionnable : ajouter/retirer un produit,
// changer une quantité, payer. Le paiement lui-même reste PayPal (Axepta est
// désactivé, cf. Bank::$active côté admin-api/customer-api).
export const Cart: React.FC<CartProps> = ({ email: emailProp }) => {

  const { authentication } = useTypedSelector((state: RootState) => state.authenticationReducer);
  const email = emailProp ?? authentication.admin?.email;

  const [ cart            , setCart            ] = useState<CartData | null>(null);
  const [ products         , setProducts        ] = useState<Product[]>([]);
  const [ loading          , setLoading         ] = useState(true);
  const [ mutating         , setMutating        ] = useState(false);
  const [ error            , setError           ] = useState<string>("");

  const [ showAddPanel     , setShowAddPanel    ] = useState(false);
  const [ productFilter    , setProductFilter   ] = useState("");
  const [ expandedProductId, setExpandedProductId ] = useState<number | null>(null);

  const [ showBankChoice   , setShowBankChoice  ] = useState(false);

  const load = async () => {
    if (!email) return;

    setLoading(true);
    setError("");

    const [ cartResponse, productsResponse ] = await Promise.all([
      getCart(email),
      getProducts(),
    ]);

    if (cartResponse.code === 1) {
      setCart(cartResponse.payload);
    } else {
      setError(cartResponse.comment || "Impossible de récupérer le panier.");
    }

    if (productsResponse.code === 1) {
      setProducts(productsResponse.payload);
    }

    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const getVariantName = (productVariantCode: string): string => {
    for (const product of products) {
      const variant = product.variants.find(v => v.code === productVariantCode);
      if (variant) {
        return variant.name ? `${product.name} — ${variant.name}` : product.name;
      }
    }
    return productVariantCode;
  };

  const handleRemove = async (item: CartItem) => {
    if (!cart) return;

    setMutating(true);
    setError("");

    const response = await removeItemFromCart(cart.id, item.productVariantCode);

    if (response.code !== 1) {
      setError(response.comment || "Impossible de retirer cet article.");
    }

    await load();
    setMutating(false);
  };

  const handleQuantityChange = async (item: CartItem, quantity: number) => {
    if (!cart || quantity <= 0) return;

    setMutating(true);
    setError("");

    const response = await changeQuantityOfItem(cart.id, item.productVariantCode, item.priceCode, quantity);

    if (response.code !== 1) {
      setError(response.comment || "Impossible de modifier la quantité.");
    }

    await load();
    setMutating(false);
  };

  const handleAdd = async (productVariantCode: string, priceCode: string) => {
    if (!cart) return;

    setMutating(true);
    setError("");

    const response = await addItemToCart(cart.id, productVariantCode, priceCode, 1);

    if (response.code !== 1) {
      setError(response.comment || "Ce produit n'est pas disponible à l'achat.");
    } else {
      setShowAddPanel(false);
      setExpandedProductId(null);
    }

    await load();
    setMutating(false);
  };

  const handlePay = async (bankCode: string) => {
    if (!cart || !email) return;

    setShowBankChoice(false);
    setMutating(true);
    setError("");

    const response = await createPayment(cart.amount, cart.id, bankCode, email, "Achat depuis admin-front");

    if (response.code === 1) {
      window.location.href = response.payload;
    } else {
      setError(response.comment || "Impossible de créer le paiement.");
      setMutating(false);
    }
  };

  const formatPrice = (cents: number): string => `${(cents / 100).toFixed(2)} €`;

  // Product.variants[].prices[].priceWithTax (api_product.ts, mapPrice) est DÉJÀ en
  // euros, pas en centimes — contrairement à CartItem/Purchase.amount ci-dessus. Bug
  // réel corrigé ici : formatPrice() rappliqué à ce champ affichait un prix 100 fois
  // trop petit (0,05 € au lieu de 5,00 €, vu en achetant un device via TierPickerModal,
  // qui lit le même champ).
  const formatCatalogPrice = (euros: number): string => `${euros.toFixed(2)} €`;

  const filteredProducts = products.filter(p =>
    productFilter === "" || p.name.toLowerCase().includes(productFilter.toLowerCase())
  );

  if (!email || loading) {
    return (
      <div className={classNames(styles.root)}>
        <AnimatedWaitingIcon2 />
      </div>
    );
  }

  return (
    <div className={classNames(styles.root)}>

      {error && (
        <div className={classNames(styles.error)}>{error}</div>
      )}

      {(!cart || cart.soldItems.length === 0) ? (
        <div className={classNames(styles.empty)}>Le panier est vide.</div>
      ) : (
        <table className={classNames(styles.table)}>
          <thead>
            <tr>
              <th>Article</th>
              <th>Prix unitaire</th>
              <th>Quantité</th>
              <th>Total</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {cart.soldItems.map((item, index) => (
              <tr key={index}>
                <td>{getVariantName(item.productVariantCode)}</td>
                <td>{formatPrice(item.priceToPayCopy)}</td>
                <td>
                  <Field
                    type="number"
                    value={item.quantity}
                    width="60px"
                    onChange={(_e, value) => handleQuantityChange(item, Number(value))}
                  />
                </td>
                <td>{formatPrice(item.totalPrice)}</td>
                <td>
                  <Trigger name="Retirer" onClick={() => handleRemove(item)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className={classNames(styles.footer)}>

        <Trigger name="Ajouter un produit" onClick={() => setShowAddPanel(true)} />

        <div className={classNames(styles.total)}>
          Total : {formatPrice(cart?.amount ?? 0)}
        </div>

        <Trigger
          name="Payer"
          onClick={() => setShowBankChoice(true)}
        />
      </div>

      {mutating && (
        <div className={classNames(styles.overlay)}>
          <AnimatedWaitingIcon2 />
        </div>
      )}

      {showAddPanel && (
        <Box onClose={() => { setShowAddPanel(false); setExpandedProductId(null); }} width="640px" height="70vh">
          <div className={classNames(styles.addPanel)}>

            <Field
              type="text"
              placeholder="Rechercher un produit..."
              value={productFilter}
              onChange={(_e, value) => setProductFilter(String(value))}
            />

            <div className={classNames(styles.productList)}>
              {filteredProducts.map(product => (
                <div key={product.id} className={classNames(styles.productBlock)}>

                  <div
                    className={classNames(styles.productHeader)}
                    onClick={() => setExpandedProductId(expandedProductId === product.id ? null : product.id)}
                  >
                    {product.name}
                  </div>

                  {expandedProductId === product.id && (
                    <div className={classNames(styles.variantList)}>
                      {product.variants.map(variant => (
                        <div key={variant.id} className={classNames(styles.variantBlock)}>
                          <div className={classNames(styles.variantName)}>{variant.name}</div>
                          {variant.prices.map(price => (
                            <Trigger
                              key={price.id}
                              name={`${price.name ?? price.code} — ${formatCatalogPrice(price.priceWithTax)}`}
                              onClick={() => variant.code && price.code && handleAdd(variant.code, price.code)}
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  )}

                </div>
              ))}
            </div>

          </div>
        </Box>
      )}

      {showBankChoice && (
        <BankChoice
          onCancel={() => setShowBankChoice(false)}
          onValidate={handlePay}
        />
      )}

    </div>
  );
};

export default Cart;
